import { describeMutation } from '@siteless/resource';
import {
  doc,
  deleteDoc,
  getDocs,
  collection,
  setDoc,
  addDoc,
  Timestamp,
} from 'firebase/firestore';
import { database } from '../../config/firebase';
import { LINK_TYPES } from '../../config/constants';
import AllLeaguesForUserResource, {
  AllLeaguesForUserResourceType,
} from './AllForUser';
import OneLeagueResource, { OneLeagueResourceType } from './One';

export let createLeagueMutation = describeMutation(
  'createLeague',
  async ({
    userId,
    name,
  }: {
    userId: string;
    name: string;
  }) => {
    try {
      let newLeague = {
        name,
        createdByUserId: userId,
        ownerUserId: userId,
        createdAt: Timestamp.fromDate(new Date()),
      };

      let newLeagueRef = await addDoc(collection(database, 'leagues'), newLeague);

      await setDoc(
        doc(
          database,
          'leagues',
          newLeagueRef.id,
          'leagueMemberUserIds',
          userId,
        ),
        { userId },
      );

      let joinLink = {
        leagueId: newLeagueRef.id,
        typeKey: LINK_TYPES.invitation.key,
      };

      let joinLinkRef = await addDoc(collection(database, 'links'), joinLink);

      await setDoc(
        newLeagueRef,
        {
          joinLinkId: joinLinkRef.id,
        },
        { merge: true },
      );

      await AllLeaguesForUserResource.refresh(userId);

      return {
        name,
        joinLinkId: joinLinkRef.id,
        leagueId: newLeagueRef.id,
      };
    } catch (error) {
      await AllLeaguesForUserResource.refresh(userId);

      window.alert(error);
      return false;
    }
  },
);

export let updateLeagueNameMutation = describeMutation(
  'updateLeagueNameMutation',
  async ({
    leagueId,
    name,
    userId,
  }: {
    leagueId: string;
    userId: string;
    name: string;
  }) => {
    try {
      await setDoc(
        doc(database, 'leagues', leagueId),
        {
          name,
        },
        { merge: true },
      );

      return true;
    } catch (error) {
      window.alert(error);
      return false;
    } finally {
      await AllLeaguesForUserResource.refresh(userId);
      await OneLeagueResource.refresh(leagueId);
    }
  },
);

export let setLeagueStrictModeMutation = describeMutation(
  'setLeagueStrictModeMutation',
  async ({
    leagueId,
    strictMode,
    userId,
  }: {
    leagueId: string;
    strictMode: boolean;
    userId: string;
  }) => {
    try {
      await OneLeagueResource.yield(
        leagueId,
        (last: OneLeagueResourceType) => ({
          ...last,
          strictMode,
        }),
      );

      await setDoc(
        doc(database, 'leagues', leagueId),
        {
          strictMode,
        },
        { merge: true },
      );

      return true;
    } catch (error) {
      window.alert(error);
      return false;
    } finally {
      await AllLeaguesForUserResource.refresh(userId);
      await OneLeagueResource.refresh(leagueId);
    }
  },
);

export let addToLeagueMutation = describeMutation(
  'addToLeague',
  async ({ userId, leagueId }: { userId: string; leagueId: string }) => {
    try {
      await setDoc(
        doc(database, 'leagues', leagueId, 'leagueMemberUserIds', userId),
        {
          userId,
        },
      );
    } catch (error) {
      window.alert(error);
    }

    await AllLeaguesForUserResource.refresh(userId);
    await OneLeagueResource.refresh(leagueId);
  },
);

export let leaveLeagueMutation = describeMutation(
  'leaveLeague',
  async ({ userId, leagueId }: { userId: string; leagueId: string }) => {
    await AllLeaguesForUserResource.yield(
      userId,
      (last: AllLeaguesForUserResourceType) =>
        last.filter(item => item.id !== leagueId),
    );
    await OneLeagueResource.yield(
      leagueId,
      (last: OneLeagueResourceType) => null,
    );
    try {
      await deleteDoc(
        doc(database, 'leagues', leagueId, 'leagueMemberUserIds', userId),
      );
    } catch (error) {
      window.alert(error);
    }

    await AllLeaguesForUserResource.refresh(userId);
    await OneLeagueResource.refresh(leagueId);
  },
);

export let kickFromLeagueMutation = describeMutation(
  'kickFromLeague',
  async ({
    leagueId,
    kickedUserId,
    ownerUserId,
  }: {
    leagueId: string;
    kickedUserId: string;
    ownerUserId: string;
  }) => {
    try {
      await deleteDoc(
        doc(database, 'leagues', leagueId, 'leagueMemberUserIds', kickedUserId),
      );
    } catch (error) {
      window.alert(error);
    }

    await AllLeaguesForUserResource.refresh(kickedUserId);
    await AllLeaguesForUserResource.refresh(ownerUserId);
    await OneLeagueResource.refresh(leagueId);
  },
);

export let deleteLeagueMutation = describeMutation(
  'deleteLeague',
  async ({ leagueId, userId }: { leagueId: string; userId: string }) => {
    try {
      let childrenPromises: Promise<any>[] = [];
      (
        await getDocs(
          collection(database, 'leagues', leagueId, 'leagueMemberUserIds'),
        )
      ).forEach(doc => {
        childrenPromises.push(
          (async () => {
            await deleteDoc(doc.ref);
          })(),
        );
      });

      await Promise.all(childrenPromises);
      await deleteDoc(doc(database, 'leagues', leagueId));
    } catch (error) {
      window.alert(error);
    }

    await AllLeaguesForUserResource.refresh(userId);
    await OneLeagueResource.refresh(leagueId);
  },
);
